/**
 * Persistence Engine - Deterministic local-first storage with corruption recovery.
 *
 * Wraps localStorage with checksums, graceful corruption handling,
 * and observable save/load events via the EventBus.
 */

import { logger }   from "@/system/logger";
import { eventBus }  from "@/runtime/eventBus";
import { runMigrations, CURRENT_ENVELOPE_VERSION, SCOPE_ENVELOPE } from "./migration/index";

const NUVRA_PREFIX = "nuvra-";

/** Simple FNV-1a hash for integrity checks. */
function checksum(data: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < data.length; i++) {
    h ^= data.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

export interface StorageEnvelope<T = unknown> {
  version: number;
  checksum: string;
  savedAt: number;
  data: T;
}

/**
 * Write data with integrity envelope.
 */
export function persistSave<T>(key: string, data: T): boolean {
  const fullKey = key.startsWith(NUVRA_PREFIX) ? key : `${NUVRA_PREFIX}${key}`;
  try {
    const payload = JSON.stringify(data);
    const envelope: StorageEnvelope<T> = {
      version: 1,
      checksum: checksum(payload),
      savedAt: Date.now(),
      data,
    };
    const serialized = JSON.stringify(envelope);
    localStorage.setItem(fullKey, serialized);
    logger.debug("Persist save", { key: fullKey, bytes: serialized.length });
    return true;
  } catch (err) {
    const reason = err instanceof Error ? err.message : "Unknown write error";
    logger.error("Persist save failed", { key: fullKey, reason });
    eventBus.emit({ type: "persistence:error", reason: `Save failed for ${fullKey}: ${reason}` });
    return false;
  }
}

/**
 * Load data with integrity verification and automatic envelope migration.
 *
 * Returns null on missing, corrupt, or unrecoverably-migrated data.
 * When a stale envelope version is detected, the migration chain is applied
 * synchronously before returning - the upgraded version is also written back
 * to storage so subsequent loads skip the migration step.
 */
export function persistLoad<T>(key: string): T | null {
  const fullKey = key.startsWith(NUVRA_PREFIX) ? key : `${NUVRA_PREFIX}${key}`;
  try {
    const raw = localStorage.getItem(fullKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    // Envelope format
    if (parsed && typeof parsed === "object" && "checksum" in parsed && "data" in parsed) {
      let envelope = parsed as StorageEnvelope<T>;

      // Upgrade envelope version if below current
      const envelopeVersion = typeof envelope.version === "number" ? envelope.version : 1;
      if (envelopeVersion < CURRENT_ENVELOPE_VERSION) {
        const result = runMigrations<StorageEnvelope<T>>({
          scope:       SCOPE_ENVELOPE,
          storageKey:  fullKey,
          fromVersion: envelopeVersion,
          toVersion:   CURRENT_ENVELOPE_VERSION,
          data:        envelope,
        });
        if (!result.success || !result.data) {
          // Migration failed - backup was restored; return null so caller uses safe default
          return null;
        }
        envelope = result.data;
        // Persist the upgraded envelope back to storage
        try {
          localStorage.setItem(fullKey, JSON.stringify(envelope));
        } catch { /* best effort */ }
      }

      const dataStr = JSON.stringify(envelope.data);
      const expectedChecksum = checksum(dataStr);

      if (envelope.checksum !== expectedChecksum) {
        logger.warn("Persist checksum mismatch - data may be corrupted", { key: fullKey });
        eventBus.emit({ type: "state:corrupted", projectId: fullKey, reason: "Checksum mismatch" });
        // Return data anyway but warn - better than losing everything
      }
      return envelope.data;
    }

    // Legacy format (pre-envelope, version 0) - run v0→v1 envelope migration
    logger.debug("Persist loaded legacy format - migrating to envelope v1", { key: fullKey });
    const legacyResult = runMigrations<StorageEnvelope<T>>({
      scope:       SCOPE_ENVELOPE,
      storageKey:  fullKey,
      fromVersion: 0,
      toVersion:   CURRENT_ENVELOPE_VERSION,
      data:        parsed,
    });
    if (!legacyResult.success || !legacyResult.data) {
      return parsed as T; // fall back to raw data rather than lose it
    }
    // Recompute checksum on the newly-wrapped data and persist
    const upgraded = legacyResult.data;
    const dataStr  = JSON.stringify(upgraded.data);
    upgraded.checksum = checksum(dataStr);
    try {
      localStorage.setItem(fullKey, JSON.stringify(upgraded));
    } catch { /* best effort */ }
    return upgraded.data;
  } catch (err) {
    const reason = err instanceof Error ? err.message : "Parse error";
    logger.error("Persist load failed", { key: fullKey, reason });
    eventBus.emit({ type: "persistence:error", reason: `Load failed for ${fullKey}: ${reason}` });

    // Attempt raw string recovery
    try {
      const raw = localStorage.getItem(fullKey);
      if (raw) {
        logger.warn("Returning raw string as fallback", { key: fullKey });
        return raw as unknown as T;
      }
    } catch { /* give up */ }

    return null;
  }
}

/**
 * Remove a persisted key.
 */
export function persistRemove(key: string): void {
  const fullKey = key.startsWith(NUVRA_PREFIX) ? key : `${NUVRA_PREFIX}${key}`;
  localStorage.removeItem(fullKey);
}

/**
 * Check if a key exists.
 */
export function persistHas(key: string): boolean {
  const fullKey = key.startsWith(NUVRA_PREFIX) ? key : `${NUVRA_PREFIX}${key}`;
  return localStorage.getItem(fullKey) !== null;
}

/**
 * List all nuvra keys in storage.
 */
export function persistKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(NUVRA_PREFIX)) keys.push(key);
  }
  return keys;
}

/**
 * Estimated bytes used by nuvra storage.
 */
export function persistUsageBytes(): number {
  let total = 0;
  for (const key of persistKeys()) {
    const val = localStorage.getItem(key);
    if (val) total += key.length + val.length;
  }
  return total * 2; // UTF-16
}
